// ============================================================
// src/pages/ShowRequests.jsx  (PPP performer app)
//
// Two tabs:
//   Shows    — incoming show booking requests from producers
//   Managers — incoming manager relationship requests
//
// Manager requests query manager_client_relationships where
// performer_id = auth user ID (= profiles.id per FK).
// No performers table lookup needed.
//
// ── CLIENT INFO ON APPROVAL ───────────────────────────────────
// When a performer taps Approve on a manager request, a form
// modal appears with the client_info fields (legal name, DOB,
// pronouns, address, fly-out city, preferred airline, numbers,
// bio, promo photos URL). They can fill it out or tap
// "Skip for now" to approve without filling it in.
//
// On submit, the component:
//   1. Upserts a row into client_info
//      (manager_id = relationship.manager.id,
//       performer_id = current user's profiles.id)
//   2. Updates manager_client_relationships.status = 'approved'
//
// The data appears immediately in ClientInfo on the manager side
// because that page queries client_info by the same key pair.
//
// ── DELETION OF OLD REQUESTS ─────────────────────────────────
// Performers can delete declined show requests, rejected manager
// requests, and rejected gig management requests. Deleting only
// removes the row from the database — it does not affect any
// gig data or existing relationships.
//
// ── SOURCE BANNERS ────────────────────────────────────────────
// Each tab shows a small info banner explaining which external
// app sends that type of request. Companion apps are currently
// works in progress — no links are shown yet.
// ============================================================

import { useState, useEffect, useCallback } from 'react'
import { useNavigate }                       from 'react-router-dom'
import { supabase }                          from '../lib/supabaseClient'
import { DateInput, TextInput, TextArea }    from '../components/FormInputs'
// adjust the relative path above if FormInputs.jsx lives elsewhere in your tree


export default function ShowRequests() {
  const navigate = useNavigate()

  // ── Tab state ─────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('shows')
  // 'shows'    = incoming show booking requests from producers
  // 'managers' = incoming manager relationship requests
  // controls which panel is visible

  // ── Show request state ────────────────────────────────────
  const [requests,        setRequests]        = useState([])
  // array of show_requests rows with nested show and producer data

  const [loading,         setLoading]         = useState(true)
  // true while the first fetch is running

  const [error,           setError]           = useState(null)
  // null = no error, string = error message to display

  const [actingOn,        setActingOn]        = useState(null)
  // the ID of the request currently being approved or declined
  // disables buttons on that row while the action is in-flight

  const [actionError,     setActionError]     = useState(null)
  // error from approving/declining a specific request

  const [showDeclined,    setShowDeclined]    = useState(false)
  // false = hide declined requests (clean default view)
  // true  = show all including declined ones

  const [conflictPending, setConflictPending] = useState(null)
  // when approving a show request creates a gig conflict, this holds
  // { request, conflicts } so the conflict modal can show before saving

  const [conflictLoading, setConflictLoading] = useState(false)
  // true while the "save anyway" path is running

  const [tripWarning,     setTripWarning]     = useState(null)
  // when the new gig's date falls inside an existing trip but the city
  // doesn't match, this holds the trip data for the warning modal

  const [deletingRequest, setDeletingRequest] = useState(null)
  // the ID of the show_requests row being deleted
  // disables buttons on that row during the delete

  // ── Manager request state ─────────────────────────────────
  const [userId,           setUserId]           = useState(null)
  // the logged-in user's auth UUID, needed for manager request queries

  const [managerRequests,  setManagerRequests]  = useState([])
  // array of manager_client_relationships rows for this user

  const [managerLoading,   setManagerLoading]   = useState(true)
  const [managerError,     setManagerError]     = useState(null)
  const [managerActingOn,  setManagerActingOn]  = useState(null)
  // ID of the manager relationship row being approved/declined
  const [managerActErr,    setManagerActErr]    = useState(null)
  const [showRejected,     setShowRejected]     = useState(false)
  // false = hide rejected manager requests by default

  const [deletingManager,  setDeletingManager]  = useState(null)
  // ID of the manager_client_relationships row being deleted

  // ── Client info modal state ───────────────────────────────
  const [clientInfoPending, setClientInfoPending] = useState(null)
  // null = modal closed
  // { relId, managerId } = modal open, collecting client info before approval

  // ── Gig management request state ─────────────────────────
  const [gigRequests,     setGigRequests]     = useState([])
  // array of management_requests rows where this user is the performer

  const [gigReqLoading,   setGigReqLoading]   = useState(true)
  const [gigReqError,     setGigReqError]     = useState(null)
  const [gigReqActingOn,  setGigReqActingOn]  = useState(null)
  // ID of the management_requests row being approved/declined
  const [gigReqActErr,    setGigReqActErr]    = useState(null)
  const [showRejectedGig, setShowRejectedGig] = useState(false)
  // false = hide rejected gig management requests by default

  const [deletingGigReq,  setDeletingGigReq]  = useState(null)
  // ID of the management_requests row being deleted

  // ── Get user ID once ──────────────────────────────────────
  // userId is needed before fetching manager and gig requests
  // because those tables use performer_id = user.id directly
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) setUserId(user.id)
    })
  }, [])

  // ── FETCH SHOW REQUESTS ───────────────────────────────────
  const fetchRequests = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const { data, error: fetchError } = await supabase
        .from('show_requests')
        .select(`
          id, role, pay, call_time, notes, music_requested, status, created_at,
          gig_id,
          shows ( id, name, venue_name, venue_address, start_time, timezone, cover_charge, age_restriction ),
          producer:performers!show_requests_performer_record_id_fkey (
            drag_name, email, ig, venmo, cashapp, paypal, zelle
          )
        `)
        .eq('performer_id', user.id)
        .order('created_at', { ascending: false })
      if (fetchError) throw fetchError
      setRequests(data || [])
    } catch (err) {
      console.error('ShowRequests: fetch error:', err)
      setError('Failed to load requests.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchRequests() }, [fetchRequests])

  // ── FETCH MANAGER REQUESTS ────────────────────────────────
  const fetchManagerRequests = useCallback(async () => {
    if (!userId) return
    setManagerLoading(true)
    setManagerError(null)
    try {
      const { data, error: fetchError } = await supabase
        .from('manager_client_relationships')
        .select(`
          id, status, message, requested_at, responded_at,
          manager:manager_profiles ( id, display_name, email, bio )
        `)
        .eq('performer_id', userId)
        .order('requested_at', { ascending: false })
      if (fetchError) throw fetchError
      setManagerRequests(data || [])
    } catch (err) {
      console.error('ManagerRequests: fetch error:', err)
      setManagerError('Failed to load manager requests.')
    } finally {
      setManagerLoading(false)
    }
  }, [userId])

  useEffect(() => { fetchManagerRequests() }, [fetchManagerRequests])

  // ── FETCH GIG MANAGEMENT REQUESTS ────────────────────────
  const fetchGigRequests = useCallback(async () => {
    if (!userId) return
    setGigReqLoading(true)
    setGigReqError(null)
    try {
      const { data, error: fetchError } = await supabase
        .from('management_requests')
        .select(`
          id, status, proposed_fee_type, proposed_fee_pct,
          proposed_fee_flat, message, manager_name, created_at,
          gig_id,
          manager:manager_profiles ( id, display_name, email ),
          gig:gigs ( id, name, venue_name, date, time, payment )
        `)
        .eq('performer_id', userId)
        .order('created_at', { ascending: false })
      if (fetchError) throw fetchError
      setGigRequests(data || [])
    } catch (err) {
      console.error('GigRequests: fetch error:', err)
      setGigReqError('Failed to load gig management requests.')
    } finally {
      setGigReqLoading(false)
    }
  }, [userId])

  useEffect(() => { fetchGigRequests() }, [fetchGigRequests])

  // ── REALTIME ──────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return
    const sub = supabase
      .channel('gig-mgmt-requests-inbox')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'management_requests', filter: `performer_id=eq.${userId}` }, fetchGigRequests)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'management_requests', filter: `performer_id=eq.${userId}` }, fetchGigRequests)
      .subscribe()
    return () => supabase.removeChannel(sub)
  }, [userId, fetchGigRequests])

  useEffect(() => {
    let sub
    async function subscribe() {
      const { data: { user } } = await supabase.auth.getUser()
      sub = supabase
        .channel('show-requests-inbox')
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'show_requests', filter: `performer_id=eq.${user.id}` }, () => fetchRequests())
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'show_requests', filter: `performer_id=eq.${user.id}` }, () => fetchRequests())
        .subscribe()
    }
    subscribe()
    return () => { if (sub) supabase.removeChannel(sub) }
  }, [fetchRequests])

  useEffect(() => {
    if (!userId) return
    const sub = supabase
      .channel('manager-requests-inbox')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'manager_client_relationships', filter: `performer_id=eq.${userId}` }, fetchManagerRequests)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'manager_client_relationships', filter: `performer_id=eq.${userId}` }, fetchManagerRequests)
      .subscribe()
    return () => supabase.removeChannel(sub)
  }, [userId, fetchManagerRequests])

  // ── SHOW REQUEST ACTIONS ──────────────────────────────────
  async function checkTripLocationMismatch(user, show, showDateStr) {
    if (!showDateStr || !show) return null
    try {
      const { data: prefs } = await supabase
        .from('notification_preferences')
        .select('trips')
        .eq('user_id', user.id)
        .single()
      const trips = prefs?.trips || []
      if (!trips.length) return null
      const norm = (s) => (s || '').toLowerCase().trim()
      for (const trip of trips) {
        let tripStart, tripEnd
        if (trip.trip_type === 'multi_leg') {
          const legs = trip.legs || []
          const departureDates = legs.map(l => l.depart_at?.split('T')[0]).filter(Boolean)
          const arrivalDates   = legs.map(l => l.arrive_at?.split('T')[0]).filter(Boolean)
          tripStart = [...departureDates, ...arrivalDates].sort()[0]
          tripEnd   = [...departureDates, ...arrivalDates].sort().at(-1)
        } else {
          tripStart = trip.depart_home_at?.split('T')[0]
          tripEnd   = trip.arrive_home_at?.split('T')[0]
        }
        if (!tripStart || !tripEnd) continue
        if (showDateStr < tripStart || showDateStr > tripEnd) continue
        const tripDest = norm(trip.destination_label || '')
        const showCity = norm(show.venue_address || show.venue_name || '')
        if (tripDest && showCity.includes(tripDest)) continue
        if (tripDest && tripDest.split(',')[0].length > 2 && showCity.includes(tripDest.split(',')[0])) continue
        return { tripLabel: trip.destination_label || 'your trip', tripDestination: trip.destination_label || '', tripStart, tripEnd }
      }
    } catch (err) { console.warn('ShowRequests: trip-check error:', err) }
    return null
  }

  async function handleAccept(requestId) {
    setActingOn(requestId)
    setActionError(null)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const req  = requests.find(r => r.id === requestId)
      const show = req?.shows
      let showDateStr = null
      if (show?.start_time) {
        const tz = show.timezone || 'America/New_York'
        showDateStr = new Date(show.start_time).toLocaleDateString('en-CA', { timeZone: tz })
      }
      const showDateFmt = show?.start_time
        ? new Date(show.start_time).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: show?.timezone || 'America/New_York' })
        : null
      if (showDateStr) {
        const { data: existing } = await supabase
          .from('gigs').select('id, name, date, time, venue_name')
          .eq('user_id', user.id).eq('date', showDateStr)
        if (existing?.length) {
          setActingOn(null)
          setConflictPending({ requestId, conflicts: existing, showName: show.name || 'this show', showDate: showDateFmt })
          return
        }
      }
      const mismatch = await checkTripLocationMismatch(user, show, showDateStr)
      if (mismatch) {
        setActingOn(null)
        setTripWarning({ requestId, showName: show?.name || 'this show', showCity: show?.venue_name || show?.venue_address || '', showDate: showDateFmt, ...mismatch })
        return
      }
      await doAccept(requestId)
    } catch (err) {
      console.error('ShowRequests: conflict-check error:', err)
      await doAccept(requestId)
    }
  }

  async function doAccept(requestId) {
    setActingOn(requestId)
    setActionError(null)
    try {
      const { error: updateErr } = await supabase
        .from('show_requests')
        .update({ status: 'accepted', updated_at: new Date().toISOString() })
        .eq('id', requestId)
      if (updateErr) throw updateErr
      setRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: 'accepted' } : r))
    } catch (err) {
      setActionError({ id: requestId, message: err.message || 'Failed to accept.' })
    } finally {
      setActingOn(null)
    }
  }

  async function handleConflictAcceptAnyway() {
    const { requestId } = conflictPending
    setConflictLoading(true)
    setConflictPending(null)
    await doAccept(requestId)
    setConflictLoading(false)
  }

  async function handleDecline(requestId) {
    setActingOn(requestId)
    setActionError(null)
    try {
      const { error } = await supabase
        .from('show_requests')
        .update({ status: 'declined', updated_at: new Date().toISOString() })
        .eq('id', requestId)
      if (error) throw error
      setRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: 'declined' } : r))
    } catch (err) {
      setActionError({ id: requestId, message: err.message || 'Failed to decline.' })
    } finally {
      setActingOn(null)
    }
  }

  // ── DELETE SHOW REQUEST ───────────────────────────────────
  async function handleDeleteShowRequest(requestId) {
    if (!window.confirm('Permanently delete this request? This cannot be undone.')) return
    setDeletingRequest(requestId)
    try {
      const { error } = await supabase
        .from('show_requests')
        .delete()
        .eq('id', requestId)
      if (error) throw error
      setRequests(prev => prev.filter(r => r.id !== requestId))
    } catch (err) {
      console.error('ShowRequests: delete show request error:', err)
      setActionError({ id: requestId, message: err.message || 'Failed to delete.' })
    } finally {
      setDeletingRequest(null)
    }
  }

  // ── MANAGER REQUEST ACTIONS ───────────────────────────────
  function handleManagerApprove(rel) {
    setClientInfoPending({
      relId:     rel.id,
      managerId: rel.manager?.id,
    })
  }

  async function doManagerApprove(relId) {
    setManagerActingOn(relId)
    setManagerActErr(null)
    try {
      const { error } = await supabase
        .from('manager_client_relationships')
        .update({ status: 'approved', responded_at: new Date().toISOString() })
        .eq('id', relId)
      if (error) throw error
      setManagerRequests(prev => prev.map(r => r.id === relId ? { ...r, status: 'approved' } : r))
    } catch (err) {
      setManagerActErr({ id: relId, message: err.message || 'Failed to approve.' })
    } finally {
      setManagerActingOn(null)
    }
  }

  async function handleClientInfoSave(relId, managerId, formData) {
    if (!userId || !managerId) {
      setClientInfoPending(null)
      await doManagerApprove(relId)
      return
    }
    try {
      const { error: infoErr } = await supabase
        .from('client_info')
        .upsert(
          {
            manager_id:        managerId,
            performer_id:      userId,
            legal_name:        formData.legal_name        || null,
            dob:               formData.dob               || null,
            pronouns:          formData.pronouns          || null,
            address:           formData.address           || null,
            fly_out_city:      formData.fly_out_city      || null,
            preferred_airline: formData.preferred_airline || null,
            airline_numbers:   formData.airline_numbers   || null,
            bio:               formData.bio               || null,
            promo_photos_url:  formData.promo_photos_url  || null,
            updated_at:        new Date().toISOString(),
          },
          { onConflict: 'manager_id,performer_id' }
        )
      if (infoErr) throw infoErr
    } catch (err) {
      console.error('[ShowRequests] client_info upsert error (non-fatal):', err.message)
    } finally {
      setClientInfoPending(null)
      await doManagerApprove(relId)
    }
  }

  async function handleClientInfoSkip(relId) {
    setClientInfoPending(null)
    await doManagerApprove(relId)
  }

  async function handleManagerReject(id) {
    setManagerActingOn(id)
    setManagerActErr(null)
    try {
      const { error } = await supabase
        .from('manager_client_relationships')
        .update({ status: 'rejected', responded_at: new Date().toISOString() })
        .eq('id', id)
      if (error) throw error
      setManagerRequests(prev => prev.map(r => r.id === id ? { ...r, status: 'rejected' } : r))
    } catch (err) {
      setManagerActErr({ id, message: err.message || 'Failed to reject.' })
    } finally {
      setManagerActingOn(null)
    }
  }

  async function handleManagerRemove(id) {
    if (!window.confirm('Remove this manager? They will lose access to your calendar.')) return
    setManagerActingOn(id)
    setManagerActErr(null)
    try {
      const { error } = await supabase
        .from('manager_client_relationships')
        .delete()
        .eq('id', id)
      if (error) throw error
      setManagerRequests(prev => prev.filter(r => r.id !== id))
    } catch (err) {
      setManagerActErr({ id, message: err.message || 'Failed to remove.' })
    } finally {
      setManagerActingOn(null)
    }
  }

  // ── DELETE MANAGER REQUEST ────────────────────────────────
  async function handleDeleteManagerRequest(id) {
    if (!window.confirm('Permanently delete this request? This cannot be undone.')) return
    setDeletingManager(id)
    try {
      const { error } = await supabase
        .from('manager_client_relationships')
        .delete()
        .eq('id', id)
      if (error) throw error
      setManagerRequests(prev => prev.filter(r => r.id !== id))
    } catch (err) {
      console.error('ShowRequests: delete manager request error:', err)
      setManagerActErr({ id, message: err.message || 'Failed to delete.' })
    } finally {
      setDeletingManager(null)
    }
  }

  // ── GIG MANAGEMENT REQUEST ACTIONS ───────────────────────
  async function handleGigReqApprove(req) {
    setGigReqActingOn(req.id)
    setGigReqActErr(null)
    try {
      const { error: reqErr } = await supabase
        .from('management_requests')
        .update({ status: 'approved', responded_at: new Date().toISOString() })
        .eq('id', req.id)
      if (reqErr) throw reqErr
      const gigUpdate = {
        is_managed:       true,
        manager_id:       req.manager?.id || null,
        manager_fee_type: req.proposed_fee_type || 'pct',
        manager_fee_pct:  req.proposed_fee_type === 'pct'  ? req.proposed_fee_pct  : null,
        manager_fee_flat: req.proposed_fee_type === 'flat' ? req.proposed_fee_flat : null,
      }
      const { error: gigErr } = await supabase
        .from('gigs')
        .update(gigUpdate)
        .eq('id', req.gig_id)
      if (gigErr) throw gigErr
      setGigRequests(prev => prev.map(r => r.id === req.id ? { ...r, status: 'approved' } : r))
    } catch (err) {
      setGigReqActErr({ id: req.id, message: err.message || 'Failed to approve.' })
    } finally {
      setGigReqActingOn(null)
    }
  }

  async function handleGigReqReject(id) {
    setGigReqActingOn(id)
    setGigReqActErr(null)
    try {
      const { error } = await supabase
        .from('management_requests')
        .update({ status: 'rejected', responded_at: new Date().toISOString() })
        .eq('id', id)
      if (error) throw error
      setGigRequests(prev => prev.map(r => r.id === id ? { ...r, status: 'rejected' } : r))
    } catch (err) {
      setGigReqActErr({ id, message: err.message || 'Failed to reject.' })
    } finally {
      setGigReqActingOn(null)
    }
  }

  // ── DELETE GIG MANAGEMENT REQUEST ─────────────────────────
  async function handleDeleteGigRequest(id) {
    if (!window.confirm('Permanently delete this request? This cannot be undone.')) return
    setDeletingGigReq(id)
    try {
      const { error } = await supabase
        .from('management_requests')
        .delete()
        .eq('id', id)
      if (error) throw error
      setGigRequests(prev => prev.filter(r => r.id !== id))
    } catch (err) {
      console.error('ShowRequests: delete gig request error:', err)
      setGigReqActErr({ id, message: err.message || 'Failed to delete.' })
    } finally {
      setDeletingGigReq(null)
    }
  }

  // ── Derived counts ────────────────────────────────────────
  const pending  = requests.filter(r => r.status === 'pending')
  const accepted = requests.filter(r => r.status === 'accepted')
  const declined = requests.filter(r => r.status === 'declined')

  const mgrPending  = managerRequests.filter(r => r.status === 'pending')
  const mgrApproved = managerRequests.filter(r => r.status === 'approved')
  const mgrRejected = managerRequests.filter(r => r.status === 'rejected')

  const gigReqPending  = gigRequests.filter(r => r.status === 'pending')
  const gigReqApproved = gigRequests.filter(r => r.status === 'approved')
  const gigReqRejected = gigRequests.filter(r => r.status === 'rejected')

  const totalPending = pending.length + mgrPending.length + gigReqPending.length

  // ── SOURCE BANNER ─────────────────────────────────────────
  // Small informational callout that explains which external app
  // sends a given type of request. Uses .requests-source-banner
  // from index.css for mobile-friendly layout.
  function SourceBanner({ icon, label, appName }) {
    return (
      <div className="requests-source-banner">
        <i className={`bi ${icon}`}></i>
        <span className="requests-source-banner-text">
          {label}{' '}
          <span className="requests-source-app">
            {appName}
            <span className="requests-coming-soon">Coming Soon</span>
          </span>
        </span>
      </div>
    )
  }

  return (
    <div className="container py-4" style={{ maxWidth: '520px' }}>

      {/* ── Page header ── */}
      <div className="requests-header">
        <div>
          <h1 className="requests-title">
            <i className="bi bi-bell"></i>Requests
          </h1>
          {totalPending > 0 && (
            <span className="requests-pending-badge">{totalPending} pending</span>
          )}
        </div>
        <button
          className="requests-refresh-btn"
          onClick={() => { fetchRequests(); fetchManagerRequests(); fetchGigRequests() }}
          aria-label="Refresh requests"
        >
          <i className="bi bi-arrow-clockwise"></i>
        </button>
      </div>

      {/* ── Tabs ──────────────────────────────────────────────
          .requests-tab-bar scrolls horizontally on mobile so
          long labels never get clipped. Each button uses
          flex-shrink:0 so it never compresses below its label
          width. A right-edge fade hints that more tabs exist.
          ──────────────────────────────────────────────────── */}
      <div className="requests-tab-bar">
        {[
          { key: 'shows',    label: 'Performance Requests', count: pending.length },
          { key: 'managers', label: 'Management Requests',  count: mgrPending.length },
          { key: 'gigreqs',  label: 'Manage a Gig',         count: gigReqPending.length },
        ].map(tab => (
          <button
            key={tab.key}
            className={`requests-tab-btn${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className="requests-tab-count">{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* ════════════════ SHOWS TAB ════════════════ */}
      {activeTab === 'shows' && (
        <>
          <SourceBanner
            icon="bi-collection-play-fill"
            label="Show requests are sent by producers using the"
            appName="Producer Pocket Planner"
          />

          {loading && (
            <div className="text-center py-5 text-muted">
              <span className="spinner-border spinner-border-sm me-2" />Loading requests...
            </div>
          )}
          {error && (
            <div className="alert alert-danger">
              <i className="bi bi-exclamation-circle me-1"></i>{error}
            </div>
          )}
          {!loading && pending.length === 0 && accepted.length === 0 && declined.length === 0 && (
            <div className="requests-empty">
              <i className="bi bi-bell"></i>
              <div className="requests-empty-title">No requests yet</div>
              <div className="requests-empty-sub">When a producer sends you a show request it will appear here.</div>
            </div>
          )}

          {pending.length > 0 && (
            <div className="mb-4">
              <p className="requests-section-label">Pending</p>
              <div className="requests-list">
                {pending.map(req => (
                  <RequestCard
                    key={req.id} request={req}
                    onAccept={handleAccept} onDecline={handleDecline}
                    actingOn={actingOn} actionError={actionError}
                  />
                ))}
              </div>
            </div>
          )}

          {accepted.length > 0 && (
            <div className="mb-4">
              <p className="requests-section-label">Accepted</p>
              <div className="requests-list">
                {accepted.map(req => (
                  <RequestCard
                    key={req.id} request={req}
                    onViewGig={() => navigate(req.gig_id ? `/gigs/${req.gig_id}` : '/gigs')}
                    actingOn={actingOn} actionError={actionError}
                  />
                ))}
              </div>
            </div>
          )}

          {declined.length > 0 && (
            <div className="mb-4">
              <button className="requests-toggle-link" onClick={() => setShowDeclined(!showDeclined)}>
                <i className={`bi bi-chevron-${showDeclined ? 'up' : 'down'}`}></i>
                {declined.length} declined request{declined.length !== 1 ? 's' : ''}
              </button>
              {showDeclined && (
                <div className="requests-list mt-3">
                  {declined.map(req => (
                    <RequestCard
                      key={req.id} request={req}
                      actingOn={actingOn} actionError={actionError}
                      onDelete={handleDeleteShowRequest}
                      deleting={deletingRequest}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ════════════════ MANAGERS TAB ════════════════ */}
      {activeTab === 'managers' && (
        <>
          <SourceBanner
            icon="bi-briefcase-fill"
            label="Manager requests are sent by managers using the"
            appName="Manager Pocket Planner"
          />

          {managerLoading && (
            <div className="text-center py-5 text-muted">
              <span className="spinner-border spinner-border-sm me-2" />Loading...
            </div>
          )}
          {managerError && (
            <div className="alert alert-danger">
              <i className="bi bi-exclamation-circle me-1"></i>{managerError}
            </div>
          )}
          {!managerLoading && managerRequests.length === 0 && (
            <div className="requests-empty">
              <i className="bi bi-briefcase"></i>
              <div className="requests-empty-title">No manager requests yet</div>
              <div className="requests-empty-sub">When a manager sends you a request it will appear here.</div>
            </div>
          )}

          {mgrPending.length > 0 && (
            <div className="mb-4">
              <p className="requests-section-label">Pending Requests</p>
              <div className="requests-list">
                {mgrPending.map(rel => (
                  <ManagerCard
                    key={rel.id} relationship={rel}
                    onApprove={() => handleManagerApprove(rel)}
                    onReject={() => handleManagerReject(rel.id)}
                    actingOn={managerActingOn} actionError={managerActErr}
                  />
                ))}
              </div>
            </div>
          )}

          {mgrApproved.length > 0 && (
            <div className="mb-4">
              {/* Section label pluralized: a performer may now have several
                  active managers at once, not just one. */}
              <p className="requests-section-label">
                Active Manager{mgrApproved.length !== 1 ? 's' : ''}
              </p>
              <div className="requests-list">
                {mgrApproved.map(rel => (
                  <ManagerCard
                    key={rel.id} relationship={rel}
                    onRemove={() => handleManagerRemove(rel.id)}
                    actingOn={managerActingOn} actionError={managerActErr}
                  />
                ))}
              </div>
            </div>
          )}

          {mgrRejected.length > 0 && (
            <div className="mb-4">
              <button className="requests-toggle-link" onClick={() => setShowRejected(!showRejected)}>
                <i className={`bi bi-chevron-${showRejected ? 'up' : 'down'}`}></i>
                {mgrRejected.length} rejected request{mgrRejected.length !== 1 ? 's' : ''}
              </button>
              {showRejected && (
                <div className="requests-list mt-3">
                  {mgrRejected.map(rel => (
                    <ManagerCard
                      key={rel.id} relationship={rel}
                      actingOn={managerActingOn} actionError={managerActErr}
                      onDelete={handleDeleteManagerRequest}
                      deleting={deletingManager}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ════════════════ GIG REQUESTS TAB ════════════════ */}
      {activeTab === 'gigreqs' && (
        <>
          <SourceBanner
            icon="bi-person-lines-fill"
            label="Gig management requests are sent by managers using the"
            appName="Manager Pocket Planner"
          />

          {gigReqLoading && (
            <div className="text-center py-5 text-muted">
              <span className="spinner-border spinner-border-sm me-2" />Loading...
            </div>
          )}
          {gigReqError && (
            <div className="alert alert-danger">
              <i className="bi bi-exclamation-circle me-1"></i>{gigReqError}
            </div>
          )}
          {!gigReqLoading && gigRequests.length === 0 && (
            <div className="requests-empty">
              <i className="bi bi-briefcase"></i>
              <div className="requests-empty-title">No gig management requests yet</div>
              <div className="requests-empty-sub">When a manager wants to manage one of your bookings, it will appear here.</div>
            </div>
          )}

          {gigReqPending.length > 0 && (
            <div className="mb-4">
              <p className="requests-section-label">Pending</p>
              <div className="requests-list">
                {gigReqPending.map(req => (
                  <GigRequestCard
                    key={req.id} request={req}
                    onApprove={() => handleGigReqApprove(req)}
                    onReject={() => handleGigReqReject(req.id)}
                    actingOn={gigReqActingOn} actionError={gigReqActErr}
                  />
                ))}
              </div>
            </div>
          )}

          {gigReqApproved.length > 0 && (
            <div className="mb-4">
              <p className="requests-section-label">Approved</p>
              <div className="requests-list">
                {gigReqApproved.map(req => (
                  <GigRequestCard
                    key={req.id} request={req}
                    actingOn={gigReqActingOn} actionError={gigReqActErr}
                  />
                ))}
              </div>
            </div>
          )}

          {gigReqRejected.length > 0 && (
            <div className="mb-4">
              <button className="requests-toggle-link" onClick={() => setShowRejectedGig(!showRejectedGig)}>
                <i className={`bi bi-chevron-${showRejectedGig ? 'up' : 'down'}`}></i>
                {gigReqRejected.length} rejected request{gigReqRejected.length !== 1 ? 's' : ''}
              </button>
              {showRejectedGig && (
                <div className="requests-list mt-3">
                  {gigReqRejected.map(req => (
                    <GigRequestCard
                      key={req.id} request={req}
                      actingOn={gigReqActingOn} actionError={gigReqActErr}
                      onDelete={handleDeleteGigRequest}
                      deleting={deletingGigReq}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── CLIENT INFO MODAL ─────────────────────────────────
          Shown when performer taps Approve on a manager request.
          Fields map 1:1 to the client_info table columns.         */}
      {clientInfoPending && (
        <ClientInfoModal
          onSave={(formData) => handleClientInfoSave(clientInfoPending.relId, clientInfoPending.managerId, formData)}
          onSkip={() => handleClientInfoSkip(clientInfoPending.relId)}
          onClose={() => setClientInfoPending(null)}
        />
      )}

      {/* ── CONFLICT MODAL ── */}
      {conflictPending && (
        <div
          className="app-modal-overlay"
          onClick={e => { if (e.target === e.currentTarget) setConflictPending(null) }}
        >
          <div className="conflict-modal">
            <div className="conflict-modal__header">
              <div className="conflict-modal__header-left">
                <i className="bi bi-exclamation-triangle-fill icon-warning"></i>
                <span className="conflict-modal__title">Schedule Conflict</span>
              </div>
              <button className="modal-close-btn" onClick={() => setConflictPending(null)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="conflict-modal__body">
              <p>
                You already have {conflictPending.conflicts.length === 1 ? 'a gig' : `${conflictPending.conflicts.length} gigs`} on{' '}
                <strong>{conflictPending.showDate}</strong>, the same day as <strong>{conflictPending.showName}</strong>.
              </p>

              <div className="conflict-gig-list">
                {conflictPending.conflicts.map(g => (
                  <div key={g.id} className="conflict-gig-item">
                    <div className="conflict-gig-item__name">{g.name || g.venue_name || 'Unnamed gig'}</div>
                    <div className="conflict-gig-item__meta">
                      {g.venue_name && g.name !== g.venue_name && (
                        <><i className="bi bi-geo-alt me-1"></i>{g.venue_name} · </>
                      )}
                      {g.time
                        ? new Date(`1970-01-01T${g.time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                        : 'Time TBD'
                      }
                    </div>
                  </div>
                ))}
              </div>

              <p className="conflict-modal__note">
                Accepting will still create a gig entry for this show. You can manage conflicts from your Gigs page.
              </p>
            </div>

            <div className="conflict-modal__footer">
              <button
                className="modal-btn--secondary"
                onClick={() => setConflictPending(null)}
                disabled={conflictLoading}
              >
                <i className="bi bi-x-lg"></i>Cancel
              </button>
              <button
                className="modal-btn--accept-anyway"
                onClick={handleConflictAcceptAnyway}
                disabled={conflictLoading}
              >
                {conflictLoading
                  ? <span className="spinner-border btn-spinner" />
                  : <><i className="bi bi-check-lg"></i>Accept Anyway</>
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── TRIP WARNING MODAL ── */}
      {tripWarning && (
        <div
          className="app-modal-overlay"
          onClick={e => { if (e.target === e.currentTarget) setTripWarning(null) }}
        >
          <div className="trip-modal">
            <div className="trip-modal__header">
              <div className="trip-modal__header-left">
                <i className="bi bi-airplane-fill icon-primary"></i>
                <span className="trip-modal__title">You're on a trip that day</span>
              </div>
              <button className="modal-close-btn" onClick={() => setTripWarning(null)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="trip-modal__body">
              <div className="trip-modal__trip-card">
                <div className="trip-modal__trip-card-title">
                  <i className="bi bi-suitcase-fill trip-modal__trip-icon"></i>
                  Trip: <strong>{tripWarning.tripLabel}</strong>
                </div>
                <div className="trip-modal__trip-dates">
                  {new Date(tripWarning.tripStart + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  {' to '}
                  {new Date(tripWarning.tripEnd + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
              </div>

              <p>
                <strong>{tripWarning.showName}</strong>{tripWarning.showDate ? ` on ${tripWarning.showDate}` : ''} is at{' '}
                <strong>{tripWarning.showCity}</strong>, which doesn't match your trip destination <strong>{tripWarning.tripDestination}</strong>.
              </p>

              <p className="trip-modal__note">
                Double-check the venue before accepting. You can still accept if this is correct.
              </p>
            </div>

            <div className="trip-modal__footer">
              <button className="modal-btn--secondary" onClick={() => setTripWarning(null)}>
                <i className="bi bi-x-lg"></i>Cancel
              </button>
              <button
                className="modal-btn--accept-anyway"
                onClick={async () => { const { requestId } = tripWarning; setTripWarning(null); await doAccept(requestId) }}
              >
                <i className="bi bi-check-lg"></i>Accept Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


// ════════════════════════════════════════════════════════════════
// ClientInfoModal
// ════════════════════════════════════════════════════════════════
// Shown when a performer approves a manager request. Every field
// now uses the shared FormInputs.jsx components (TextInput,
// TextArea, DateInput) instead of raw <input>/<textarea> with
// one-off client-info-input classes, so this form picks up the
// exact same styling as every other form in the app.
//
// Field types: Legal Name, Pronouns, Fly-Out City, Preferred
// Airline, and Promo Photos URL are single-line (TextInput).
// Mailing Address, Airline/FF Numbers, and Bio are multi-line
// (TextArea). Date of Birth uses the calendar-popover DateInput.

const EMPTY_INFO = {
  legal_name: '', dob: '', pronouns: '', address: '',
  fly_out_city: '', preferred_airline: '', airline_numbers: '',
  bio: '', promo_photos_url: '',
}

function ClientInfoModal({ onSave, onSkip, onClose }) {
  const [form,   setForm]   = useState(EMPTY_INFO)
  const [saving, setSaving] = useState(false)

  function set(field, value) { setForm(prev => ({ ...prev, [field]: value })) }

  async function handleSave() {
    setSaving(true)
    await onSave(form)
    setSaving(false)
  }

  return (
    <div
      className="client-info-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="client-info-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="client-info-modal__header">
          <div className="client-info-modal__header-row">
            <div>
              <div className="client-info-modal__eyebrow">
                <i className="bi bi-person-vcard-fill" style={{ marginRight: '0.35rem' }} />
                Manager Request Approved
              </div>
              <div className="client-info-modal__title">
                Share your info with your manager
              </div>
            </div>
            <button className="client-info-modal__close" onClick={onClose}>
              <i className="bi bi-x-lg" />
            </button>
          </div>
          <p className="client-info-modal__subtitle">
            This helps your manager book you accurately. All fields are optional, tap Skip to approve without sharing anything now.
          </p>
        </div>

        {/* Form body — every field uses the shared FormInputs.jsx
            components so this modal matches every other form in
            the app. */}
        <div className="client-info-modal__body">

          {/* Legal name (one line) + Date of Birth (calendar popover) */}
          <div className="client-info-modal__grid-2">
            <TextInput
              label="Legal Name"
              placeholder="Full legal name"
              value={form.legal_name}
              onChange={e => set('legal_name', e.target.value)}
              name="legal_name"
            />
            {/* DateInput renders its own label internally (via its `label`
                prop). It emits { target: { value, name } }, the same shape
                as a native input's onChange, so `set` works unchanged. */}
            <DateInput
              label="Date of Birth"
              value={form.dob}
              onChange={e => set('dob', e.target.value)}
              name="dob"
            />
          </div>

          {/* Pronouns — one line */}
          <TextInput
            label="Pronouns"
            placeholder="she/her, they/them"
            value={form.pronouns}
            onChange={e => set('pronouns', e.target.value)}
            name="pronouns"
          />

          {/* Mailing address — textarea; street + city/state/zip rarely
              fits on one line */}
          <TextArea
            label="Mailing Address"
            placeholder="Street, City, State, ZIP"
            value={form.address}
            onChange={e => set('address', e.target.value)}
            rows={2}
            name="address"
          />

          {/* Travel info */}
          <div className="client-info-travel-section">
            <span className="client-info-travel-title">
              <i className="bi bi-airplane-fill" style={{ marginRight: '0.3rem' }} />Travel Info
            </span>
            <div className="client-info-travel-fields">
              <TextInput
                label="Fly-Out City"
                placeholder="New York, Los Angeles"
                value={form.fly_out_city}
                onChange={e => set('fly_out_city', e.target.value)}
                name="fly_out_city"
              />
              <div className="client-info-modal__grid-2">
                <TextInput
                  label="Preferred Airline"
                  placeholder="Delta, Southwest"
                  value={form.preferred_airline}
                  onChange={e => set('preferred_airline', e.target.value)}
                  name="preferred_airline"
                />
                <TextArea
                  label="Airline / FF Numbers"
                  placeholder="Loyalty number(s)"
                  value={form.airline_numbers}
                  onChange={e => set('airline_numbers', e.target.value)}
                  rows={2}
                  name="airline_numbers"
                />
              </div>
            </div>
          </div>

          {/* Bio — textarea */}
          <TextArea
            label="Bio / Notes for Manager"
            placeholder="Anything your manager should know about you..."
            value={form.bio}
            onChange={e => set('bio', e.target.value)}
            rows={3}
            name="bio"
          />

          {/* Promo photos URL — one line */}
          <TextInput
            label="Promo Photos URL"
            placeholder="Google Drive, Dropbox, etc."
            value={form.promo_photos_url}
            onChange={e => set('promo_photos_url', e.target.value)}
            type="url"
            name="promo_photos_url"
          />

        </div>

        {/* Footer */}
        <div className="client-info-modal__footer">
          <button
            className="modal-btn--secondary"
            onClick={() => onSkip()}
            disabled={saving}
          >
            <i className="bi bi-skip-forward" />Skip for now
          </button>
          <button
            className="modal-btn--primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving
              ? <span className="spinner-border btn-spinner" />
              : <><i className="bi bi-check-lg" />Save & Approve</>
            }
          </button>
        </div>

      </div>
    </div>
  )
}


// ── SHOW REQUEST CARD ─────────────────────────────────────────
// Uses .req-card* classes from index.css.
// Avatar colour is driven by className (req-card__avatar--{status})
// rather than the former inline sc.bg / sc.dot style objects.
// onDelete / deleting are only passed in for declined cards.
function RequestCard({ request, onAccept, onDecline, onViewGig, actingOn, actionError, onDelete, deleting }) {
  const [expanded, setExpanded] = useState(false)
  const isActing   = actingOn === request.id
  const isDeleting = deleting === request.id
  const thisError  = actionError?.id === request.id ? actionError.message : null
  const show       = request.shows
  const tz         = show?.timezone || 'America/New_York'

  const showDate    = show?.start_time ? new Date(show.start_time).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz }) : 'Date TBD'
  const showTime    = show?.start_time ? new Date(show.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz }) : ''
  const callTimeFmt = request.call_time ? new Date(request.call_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz }) : null

  const iconMap = { pending: 'bi-bell-fill', accepted: 'bi-check-circle-fill', declined: 'bi-x-circle-fill' }

  return (
    <div className={`req-card${request.status === 'pending' ? ' req-card--pending' : ''}${request.status === 'declined' ? ' req-card--faded' : ''}`}>
      <div className={`req-card__avatar req-card__avatar--${request.status}`}>
        <i className={`bi ${iconMap[request.status] || 'bi-bell-fill'}`}></i>
      </div>

      <div className="req-card__body">
        <div className="req-card__title-row">
          <span className="req-card__name">{show?.name || 'Untitled Show'}</span>
          {request.producer && (
            <span className="req-card__sub">· booked by <strong>{request.producer.drag_name}</strong></span>
          )}
        </div>

        <div className="req-card__meta">
          {show?.start_time && (
            <span><i className="bi bi-calendar3 me-1"></i>{showDate}{showTime ? ` · ${showTime}` : ''}</span>
          )}
          {show?.venue_name && (
            <span><i className="bi bi-geo-alt me-1"></i>{show.venue_name}</span>
          )}
          {request.pay != null && (
            <span className={request.pay > 0 ? 'req-card__meta-green' : undefined}>
              <i className="bi bi-cash me-1"></i>{request.pay > 0 ? `$${Number(request.pay).toFixed(2)}` : 'Unpaid'}
            </span>
          )}
          {request.music_requested && (
            <span className="req-card__meta-orange"><i className="bi bi-music-note-beamed me-1"></i>Music needed</span>
          )}
        </div>

        {expanded && (
          <div className="req-card__detail">
            {show?.venue_address && <div><i className="bi bi-pin-map me-1 text-muted"></i>{show.venue_address}</div>}
            {request.role       && <div><i className="bi bi-person-badge me-1 text-muted"></i>Role: <strong>{request.role}</strong></div>}
            {callTimeFmt        && <div><i className="bi bi-clock me-1 text-muted"></i>Call time: <strong>{callTimeFmt}</strong></div>}
            {show?.cover_charge && <div><i className="bi bi-ticket me-1 text-muted"></i>Cover: <strong>{show.cover_charge}</strong></div>}
            {show?.age_restriction && <div><i className="bi bi-person-check me-1 text-muted"></i>Age: <strong>{show.age_restriction}</strong></div>}
            {request.producer?.email && <div><i className="bi bi-envelope me-1 text-muted"></i>{request.producer.email}</div>}
            {request.notes && (
              <div className="req-card__detail-note">
                <i className="bi bi-chat-left-text me-1 text-muted"></i>{request.notes}
              </div>
            )}
          </div>
        )}

        {thisError && (
          <div className="req-card__error">
            <i className="bi bi-exclamation-circle me-1"></i>{thisError}
          </div>
        )}

        <div className="req-card__actions">
          {request.status === 'pending' && (
            <>
              <button className="req-btn req-btn--accept" onClick={() => onAccept(request.id)} disabled={isActing}>
                {isActing
                  ? <span className="spinner-border btn-spinner" />
                  : <><i className="bi bi-check-lg"></i>Accept</>
                }
              </button>
              <button className="req-btn req-btn--decline" onClick={() => onDecline(request.id)} disabled={isActing}>
                <i className="bi bi-x-lg"></i>Decline
              </button>
            </>
          )}

          {request.status === 'accepted' && onViewGig && (
            <button className="req-btn req-btn--view-gig" onClick={onViewGig}>
              <i className="bi bi-calendar-check"></i>View in Gigs
            </button>
          )}

          {request.status === 'declined' && onDelete && (
            <button className="req-btn req-btn--delete" onClick={() => onDelete(request.id)} disabled={isDeleting}>
              {isDeleting
                ? <span className="spinner-border btn-spinner" />
                : <><i className="bi bi-trash3"></i>Delete</>
              }
            </button>
          )}

          <button className="req-btn req-btn--details" onClick={() => setExpanded(p => !p)}>
            {expanded ? 'Less' : 'Details'}
            <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`}></i>
          </button>
        </div>
      </div>
    </div>
  )
}


// ── MANAGER CARD ──────────────────────────────────────────────
// Uses .req-card* classes from index.css.
// onDelete / deleting are only passed in for rejected cards.
function ManagerCard({ relationship, onApprove, onReject, onRemove, actingOn, actionError, onDelete, deleting }) {
  const [expanded, setExpanded] = useState(false)
  const isActing   = actingOn === relationship.id
  const isDeleting = deleting === relationship.id
  const thisError  = actionError?.id === relationship.id ? actionError.message : null
  const manager    = relationship.manager

  const requestedDate = relationship.requested_at
    ? new Date(relationship.requested_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null

  const iconMap = { pending: 'bi-briefcase-fill', approved: 'bi-check-circle-fill', rejected: 'bi-x-circle-fill' }

  return (
    <div className={`req-card${relationship.status === 'pending' ? ' req-card--pending' : ''}${relationship.status === 'rejected' ? ' req-card--faded' : ''}`}>
      <div className={`req-card__avatar req-card__avatar--${relationship.status}`}>
        <i className={`bi ${iconMap[relationship.status] || 'bi-briefcase-fill'}`}></i>
      </div>

      <div className="req-card__body">
        <div className="req-card__title-row">
          <span className="req-card__name">{manager?.display_name || 'Unknown Manager'}</span>
          {relationship.status === 'approved' && (
            <span className="mgr-status--active">· Active</span>
          )}
          {relationship.status === 'rejected' && (
            <span className="req-card__sub">· Rejected</span>
          )}
        </div>

        <div className="req-card__meta">
          {requestedDate && <span><i className="bi bi-calendar3 me-1"></i>Requested {requestedDate}</span>}
          {manager?.email && <span><i className="bi bi-envelope me-1"></i>{manager.email}</span>}
        </div>

        {expanded && (
          <div className="req-card__detail">
            {manager?.bio && <div><i className="bi bi-person-lines-fill me-1 text-muted"></i><strong>About:</strong> {manager.bio}</div>}
            {relationship.message && (
              <div className="req-card__detail-note">
                <i className="bi bi-chat-left-text me-1 text-muted"></i>{relationship.message}
              </div>
            )}
            {!manager?.bio && !relationship.message && (
              <span className="req-card__detail-empty">No additional information provided.</span>
            )}
          </div>
        )}

        {thisError && (
          <div className="req-card__error">
            <i className="bi bi-exclamation-circle me-1"></i>{thisError}
          </div>
        )}

        <div className="req-card__actions">
          {relationship.status === 'pending' && (
            <>
              <button className="req-btn req-btn--accept" onClick={onApprove} disabled={isActing}>
                {isActing
                  ? <span className="spinner-border btn-spinner" />
                  : <><i className="bi bi-check-lg"></i>Approve</>
                }
              </button>
              <button className="req-btn req-btn--decline" onClick={onReject} disabled={isActing}>
                <i className="bi bi-x-lg"></i>Reject
              </button>
            </>
          )}

          {relationship.status === 'approved' && onRemove && (
            <button className="req-btn req-btn--remove" onClick={onRemove} disabled={isActing}>
              {isActing
                ? <span className="spinner-border btn-spinner" />
                : <><i className="bi bi-person-dash"></i>Remove</>
              }
            </button>
          )}

          {relationship.status === 'rejected' && onDelete && (
            <button className="req-btn req-btn--delete" onClick={() => onDelete(relationship.id)} disabled={isDeleting}>
              {isDeleting
                ? <span className="spinner-border btn-spinner" />
                : <><i className="bi bi-trash3"></i>Delete</>
              }
            </button>
          )}

          {(manager?.bio || relationship.message) && (
            <button className="req-btn req-btn--details" onClick={() => setExpanded(p => !p)}>
              {expanded ? 'Less' : 'Details'}
              <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`}></i>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}


// ── GIG REQUEST CARD ──────────────────────────────────────────
// Uses .req-card* classes from index.css.
// onDelete / deleting are only passed in for rejected cards.
function GigRequestCard({ request, onApprove, onReject, actingOn, actionError, onDelete, deleting }) {
  const [expanded, setExpanded] = useState(false)
  const isActing   = actingOn === request.id
  const isDeleting = deleting === request.id
  const thisError  = actionError?.id === request.id ? actionError.message : null
  const manager    = request.manager
  const managerLabel = manager?.display_name || request.manager_name || 'Unknown Manager'
  const gig = request.gig
  const gigDate = gig?.date
    ? new Date(gig.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
    : null

  const feeLabel = (() => {
    if (request.proposed_fee_type === 'flat' && request.proposed_fee_flat != null) return `$${parseFloat(request.proposed_fee_flat).toFixed(2)} flat fee`
    if (request.proposed_fee_type === 'pct'  && request.proposed_fee_pct  != null) return `${parseFloat(request.proposed_fee_pct)}% of booking`
    return 'Fee not specified'
  })()

  const gigPayment = parseFloat(gig?.payment || 0)
  const feeAmount  = request.proposed_fee_type === 'flat'
    ? parseFloat(request.proposed_fee_flat || 0)
    : gigPayment * (parseFloat(request.proposed_fee_pct || 0) / 100)
  const takeHome = gigPayment - feeAmount

  const iconMap = { pending: 'bi-briefcase-fill', approved: 'bi-check-circle-fill', rejected: 'bi-x-circle-fill' }

  return (
    <div className={`req-card${request.status === 'pending' ? ' req-card--pending' : ''}${request.status === 'rejected' ? ' req-card--faded' : ''}`}>
      <div className={`req-card__avatar req-card__avatar--${request.status}`}>
        <i className={`bi ${iconMap[request.status] || 'bi-briefcase-fill'}`}></i>
      </div>

      <div className="req-card__body">
        <div className="req-card__title-row">
          <span className="req-card__name">{gig?.name || gig?.venue_name || 'Unnamed Gig'}</span>
          <span className="req-card__sub">· managed by <strong>{managerLabel}</strong></span>
          {request.status === 'approved' && (
            <span className="gig-status--approved">· Approved</span>
          )}
          {request.status === 'rejected' && (
            <span className="req-card__sub">· Rejected</span>
          )}
        </div>

        <div className="req-card__meta">
          {gig?.venue_name && <span><i className="bi bi-geo-alt me-1"></i>{gig.venue_name}</span>}
          {gigDate          && <span><i className="bi bi-calendar3 me-1"></i>{gigDate}</span>}
          <span className="req-card__meta-pink"><i className="bi bi-briefcase me-1"></i>{feeLabel}</span>
          {gigPayment > 0 && (
            <span className="req-card__meta-green"><i className="bi bi-cash me-1"></i>you keep ${takeHome.toFixed(2)}</span>
          )}
        </div>

        {expanded && (
          <div className="req-card__detail">
            {manager?.email && <div><i className="bi bi-envelope me-1 text-muted"></i>{manager.email}</div>}
            {gigPayment > 0 && (
              <div className="req-card__detail-breakdown">
                <div className="req-card__detail-breakdown-row">
                  <span className="req-breakdown-label">Booking fee</span>
                  <strong>${gigPayment.toFixed(2)}</strong>
                </div>
                <div className="req-card__detail-breakdown-row">
                  <span className="req-breakdown-label">Manager fee ({feeLabel})</span>
                  <span className="req-breakdown-cut">&minus;${feeAmount.toFixed(2)}</span>
                </div>
                <div className="req-card__detail-breakdown-total">
                  <span>You keep</span>
                  <span className="req-breakdown-keep">${takeHome.toFixed(2)}</span>
                </div>
              </div>
            )}
            {request.message && (
              <div className="req-card__detail-note">
                <i className="bi bi-chat-left-text me-1 text-muted"></i>{request.message}
              </div>
            )}
          </div>
        )}

        {thisError && (
          <div className="req-card__error">
            <i className="bi bi-exclamation-circle me-1"></i>{thisError}
          </div>
        )}

        <div className="req-card__actions">
          {request.status === 'pending' && (
            <>
              <button className="req-btn req-btn--accept" onClick={onApprove} disabled={isActing}>
                {isActing
                  ? <span className="spinner-border btn-spinner" />
                  : <><i className="bi bi-check-lg"></i>Approve</>
                }
              </button>
              <button className="req-btn req-btn--decline" onClick={onReject} disabled={isActing}>
                <i className="bi bi-x-lg"></i>Reject
              </button>
            </>
          )}

          {request.status === 'rejected' && onDelete && (
            <button className="req-btn req-btn--delete" onClick={() => onDelete(request.id)} disabled={isDeleting}>
              {isDeleting
                ? <span className="spinner-border btn-spinner" />
                : <><i className="bi bi-trash3"></i>Delete</>
              }
            </button>
          )}

          {(request.message || gigPayment > 0 || manager?.email) && (
            <button className="req-btn req-btn--details" onClick={() => setExpanded(p => !p)}>
              {expanded ? 'Less' : 'Details'}
              <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`}></i>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}